import { useState, useMemo } from 'react';
import Navbar from './Navbar';
import ContactSupportModal from './ContactSupportModal';
import MyTickets from './MyTickets';
import { supportApi } from '../api/Supportapi';

const GRADIENT = 'bg-gradient-to-r from-[#5B2EFF] via-[#8B5CF6] to-[#FF4D8D]';

const Svg = ({ children, className = 'w-5 h-5' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    {children}
  </svg>
);

const SearchIcon = () => (<Svg><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></Svg>);
const CloseIcon = () => (<Svg className="w-4 h-4"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></Svg>);
const MessageIcon = () => (<Svg><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></Svg>);
const BookIcon = () => (<Svg><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></Svg>);
const TicketIcon = () => (<Svg><path d="M2 9a3 3 0 0 1 0 6v3a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1v-3a3 3 0 0 1 0-6V6a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1z" /><path d="M13 5v2M13 17v2M13 11v2" /></Svg>);
const ChevronDownIcon = ({ className = 'w-4 h-4' }) => (<Svg className={className}><polyline points="6 9 12 15 18 9" /></Svg>);
const ThumbsUpIcon = () => (<Svg className="w-3.5 h-3.5"><path d="M7 10v12" /><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z" /></Svg>);
const ThumbsDownIcon = () => (<Svg className="w-3.5 h-3.5"><path d="M17 14V2" /><path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L13 22h0a3.13 3.13 0 0 1-3-3.88Z" /></Svg>);

const CategoryIcons = {
  all: <Svg><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></Svg>,
  profile: <Svg><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></Svg>,
  applications: <Svg><rect x="2" y="7" width="20" height="14" rx="2" ry="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></Svg>,
  safety: <Svg><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></Svg>,
  security: <Svg><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></Svg>,
  support: <Svg><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" /></Svg>,
};

const CATEGORIES = [
  { id: 'all', title: 'All questions', color: 'bg-purple-50 text-[#7C3AED]' },
  { id: 'profile', title: 'Account & Profile', color: 'bg-purple-50 text-[#7C3AED]' },
  { id: 'applications', title: 'Jobs & Applications', color: 'bg-pink-50 text-[#DB2777]' },
  { id: 'safety', title: 'Safety & Reporting', color: 'bg-violet-50 text-[#5B2EFF]' },
  { id: 'security', title: 'Account & Security', color: 'bg-amber-50 text-amber-600' },
  { id: 'support', title: 'Support', color: 'bg-rose-50 text-rose-600' },
];

const CATEGORY_TITLES = Object.fromEntries(CATEGORIES.map((c) => [c.id, c.title]));

const POPULAR_SEARCHES = ['resume', 'apply', 'OTP', 'password', 'ticket'];

const FAQS = [
  { cat: 'profile', q: 'How do I create an account?', a: 'Click Register / Sign Up, select Job Seeker, enter your mobile number or email address, verify it using OTP, and complete your profile.' },
  { cat: 'profile', q: 'Can I log in using my mobile number or email?', a: 'Yes. You can log in using any of the following:', list: ['Mobile + OTP', 'Mobile + Password', 'Email + OTP', 'Email + Password'] },
  { cat: 'profile', q: 'How do I create my job profile?', a: 'After registration, go to your Job Seeker Dashboard and complete your profile with your personal details, education, skills, experience, resume, and preferred job locations.' },
  { cat: 'profile', q: 'How can I upload or update my resume?', a: 'Go to Profile \u2192 Resume and upload your latest resume. You can replace your existing resume whenever required.' },
  { cat: 'profile', q: 'I forgot my password. What should I do?', a: 'Click Forgot Password on the login page and follow the verification steps sent to your registered mobile number or email address.' },
  { cat: 'profile', q: 'Can I change my registered mobile number or email?', a: 'Yes, if the feature is available in your account settings. The new mobile number or email must be verified using OTP.' },
  { cat: 'profile', q: "Why haven't I received an OTP?", a: 'Check your mobile network or email inbox and spam folder. Wait for the OTP timer to finish and use Resend OTP. If the problem continues, contact PlanB Jobs Support.' },

  { cat: 'applications', q: 'How do I search for jobs?', a: 'Use the Search Jobs option and search by job title, skills, company, location, experience, or other relevant criteria.' },
  { cat: 'applications', q: 'How do I apply for a job?', a: "Open the job you're interested in and click Apply Now. Make sure your profile and resume are updated before applying." },
  { cat: 'applications', q: 'How can I check my job applications?', a: "Go to Dashboard \u2192 My Applications to view the jobs you've applied for and check their current status." },
  { cat: 'applications', q: 'Can I edit my profile after applying for a job?', a: 'Yes. You can update your profile and resume from your dashboard. However, information already submitted with an application may not change for applications that have already been processed.' },
  { cat: 'applications', q: 'Can I apply for multiple jobs?', a: 'Yes. You can apply for multiple suitable jobs using your PlanB Jobs profile.' },
  { cat: 'applications', q: 'How do I know whether my application was shortlisted?', a: 'Application status can be checked under My Applications. The employer may update the status as your application progresses.' },
  { cat: 'applications', q: 'Can I withdraw my job application?', a: 'If the withdrawal option is available for that application, you can withdraw it from My Applications. Otherwise, contact support.' },

  { cat: 'safety', q: 'How do I report a suspicious or inappropriate job?', a: 'Open the job listing and use the Report Job option. Provide the reason and relevant details, and our team will review the report.' },
  { cat: 'safety', q: 'Is PlanB Jobs responsible for hiring me?', a: 'No. PlanB Jobs provides the platform for job seekers and employers to connect. Final shortlisting, interviews, and hiring decisions are made by the employer.' },

  { cat: 'security', q: 'Is my personal information safe?', a: 'PlanB Jobs uses appropriate security measures to protect account information. Users should also keep their passwords and OTPs confidential.' },
  { cat: 'security', q: 'Should I share my OTP with anyone?', a: 'No. Never share your OTP or password with anyone, including someone claiming to be a PlanB Jobs representative.' },
  { cat: 'security', q: 'What should I do if I suspect someone has accessed my account?', a: 'Immediately change your password and contact PlanB Jobs Support if you believe your account has been compromised.' },
  { cat: 'security', q: 'Why is OTP verification required?', a: 'OTP verification helps confirm that the mobile number or email belongs to the account holder and provides an additional layer of account security.' },

  { cat: 'support', q: 'How can I contact PlanB Jobs Support?', a: 'Go to Dashboard \u2192 Help & Support \u2192 Contact Support and submit your enquiry.' },
  { cat: 'support', q: 'What information should I include in a support request?', a: 'Provide your name, registered email/mobile number, issue category, and a clear description of the problem. You may also attach relevant screenshots or documents.' },
  { cat: 'support', q: 'How can I check my support request?', a: 'You can view submitted support requests and their status from the Help & Support section of your dashboard.' },
  { cat: 'support', q: 'What are the support ticket statuses?', a: 'New \u2192 Assigned \u2192 In Progress \u2192 Resolved \u2192 Closed' },
  { cat: 'support', q: 'What should I do if my issue is not resolved?', a: 'Reply to the existing support request with additional information, or contact support again using your ticket/reference number.' },
];

// Stable key used to attribute "Was this helpful?" votes to a specific FAQ on the backend.
function faqKey(item) {
  return `job_seeker:${item.cat}:${item.q.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`;
}

function FaqItem({ item, isOpen, onToggle, showCategory }) {
  const [feedback, setFeedback] = useState(null); // null | 'yes' | 'no'
  const [sending, setSending] = useState(false);

  const vote = async (value) => {
    if (sending || feedback) return;
    setSending(true);
    setFeedback(value); // optimistic
    try {
      await supportApi.submitFaqFeedback({
        faqKey: faqKey(item),
        category: item.cat,
        helpful: value === 'yes',
      });
    } catch {
      // Feedback is non-critical; keep the optimistic UI state even if the request fails.
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className={`bg-white border rounded-2xl overflow-hidden transition-all duration-200 ${
        isOpen ? 'border-[#8B5CF6]/40 shadow-lg shadow-purple-500/10' : 'border-purple-100 shadow-sm hover:shadow-md hover:shadow-purple-500/10'
      }`}
    >
      <button type="button" onClick={onToggle} className="w-full flex items-center gap-4 px-5 py-4 text-left">
        <span className="flex-1 min-w-0">
          {showCategory && (
            <span className="block text-xs font-semibold text-[#7C3AED] mb-0.5">{CATEGORY_TITLES[item.cat]}</span>
          )}
          <span className="block text-sm font-semibold text-[#0D1B3D]">{item.q}</span>
        </span>
        <span
          className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
            isOpen ? `${GRADIENT} text-white rotate-180` : 'bg-purple-50 text-[#7C3AED]'
          }`}
        >
          <ChevronDownIcon className="w-4 h-4" />
        </span>
      </button>

      {isOpen && (
        <div className="px-5 pb-5 border-t border-purple-50">
          <p className="text-sm leading-relaxed text-gray-600 pt-4">{item.a}</p>
          {item.list && (
            <ul className="list-disc pl-5 mt-2 space-y-1">
              {item.list.map((li) => (
                <li key={li} className="text-sm leading-relaxed text-gray-600">{li}</li>
              ))}
            </ul>
          )}

          <div className="mt-4 pt-3 border-t border-purple-50 flex items-center gap-3">
            {feedback === null ? (
              <>
                <span className="text-xs text-gray-400">Was this helpful?</span>
                <button
                  type="button"
                  onClick={() => vote('yes')}
                  className="flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-[#7C3AED] hover:bg-purple-50 border border-gray-200 hover:border-[#8B5CF6] rounded-full px-3 py-1 transition-colors"
                >
                  <ThumbsUpIcon /> Yes
                </button>
                <button
                  type="button"
                  onClick={() => vote('no')}
                  className="flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-rose-600 hover:bg-rose-50 border border-gray-200 hover:border-rose-300 rounded-full px-3 py-1 transition-colors"
                >
                  <ThumbsDownIcon /> No
                </button>
              </>
            ) : (
              <span className="text-xs font-medium text-[#7C3AED]">
                {feedback === 'yes' ? 'Glad it helped!' : 'Thanks \u2014 we\u2019ll work on improving this.'}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function HelpCard({ onContact }) {
  return (
    <div className="rounded-2xl p-5 text-white bg-gradient-to-br from-[#5B2EFF] via-[#8B5CF6] to-[#FF4D8D] shadow-xl shadow-purple-500/25">
      <h3 className="text-base font-bold">Still need help?</h3>
      <p className="text-sm text-white/90 mt-1">Our support team usually responds within 24 hours.</p>
      <button
        type="button"
        onClick={onContact}
        className="mt-4 w-full flex items-center justify-center gap-2 bg-white text-[#7C3AED] text-sm font-bold px-4 py-2.5 rounded-full shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-200"
      >
        <MessageIcon /> Contact Support
      </button>
    </div>
  );
}

function JobSeekerHelpSupport() {
  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [openKey, setOpenKey] = useState(null);
  const [view, setView] = useState('faq'); // 'faq' | 'tickets'
  const [modalOpen, setModalOpen] = useState(false);
  const [ticketsRefreshKey, setTicketsRefreshKey] = useState(0);

  const term = search.trim().toLowerCase();

  const counts = useMemo(() => {
    const c = { all: FAQS.length };
    FAQS.forEach((f) => { c[f.cat] = (c[f.cat] || 0) + 1; });
    return c;
  }, []);

  const visibleFaqs = useMemo(() => {
    if (term) return FAQS.filter((f) => f.q.toLowerCase().includes(term) || f.a.toLowerCase().includes(term));
    if (activeCategory === 'all') return FAQS;
    return FAQS.filter((f) => f.cat === activeCategory);
  }, [term, activeCategory]);

  const selectCategory = (id) => {
    setActiveCategory(id);
    setSearch('');
    setOpenKey(null);
  };

  const handleSearch = (value) => {
    setSearch(value);
    setView('faq');
    setOpenKey(null);
  };

  const quickActions = [
    { id: 'faq', title: 'Browse FAQs', desc: 'Answers to common questions', icon: <BookIcon />, onClick: () => setView('faq'), active: view === 'faq' },
    { id: 'tickets', title: 'My Tickets', desc: 'Track your support requests', icon: <TicketIcon />, onClick: () => setView('tickets'), active: view === 'tickets' },
    { id: 'contact', title: 'Contact Support', desc: 'Send us a new request', icon: <MessageIcon />, onClick: () => setModalOpen(true), active: false },
  ];

  const listTitle = term
    ? `${visibleFaqs.length} result${visibleFaqs.length !== 1 ? 's' : ''} for \u201c${search.trim()}\u201d`
    : CATEGORY_TITLES[activeCategory];

  return (
    <div className="min-h-screen bg-[#FDF8FF]">
      <Navbar user={user} />

      <div className="max-w-6xl mx-auto px-4 py-6 sm:py-8 space-y-6">
        {/* Hero: title + search */}
        <section className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-[#C4B5FD] via-[#DDD6FE] to-[#F9A8D4] border border-purple-200 px-5 py-4 sm:px-8 sm:py-5 shadow-lg shadow-purple-500/10">
          <div className="absolute -top-16 -right-10 w-64 h-64 rounded-full bg-white/30 blur-2xl" />
          <div className="absolute -bottom-20 left-1/3 w-56 h-56 rounded-full bg-white/25 blur-2xl" />
          <div className="relative max-w-2xl">
            <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-[#5B2EFF] via-[#8B5CF6] to-[#FF4D8D] bg-clip-text text-transparent">Hi, how can we help you?</h1>
            <p className="mt-1 text-sm text-gray-700">Search our answers, or reach the support team if you can&rsquo;t find what you need.</p>

            <div className="mt-3 flex items-center bg-white rounded-full border border-purple-100 shadow-md shadow-purple-500/10 overflow-hidden">
              <span className="pl-4 text-gray-400 shrink-0"><SearchIcon /></span>
              <input
                type="text"
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search for resume, apply, password..."
                className="flex-1 min-w-0 py-2.5 px-3 outline-none text-sm text-[#0D1B3D] bg-transparent"
              />
              {search && (
                <button type="button" onClick={() => handleSearch('')} aria-label="Clear search" className="mr-3 p-1.5 rounded-full text-gray-400 hover:bg-purple-50 hover:text-[#7C3AED] transition">
                  <CloseIcon />
                </button>
              )}
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-700">Popular:</span>
              {POPULAR_SEARCHES.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => handleSearch(p)}
                  className="text-xs font-medium px-3 py-1 rounded-full bg-white text-[#7C3AED] border border-purple-100 hover:bg-purple-50 transition"
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Quick actions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {quickActions.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={a.onClick}
              className={`group flex items-center gap-4 rounded-2xl border p-4 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-purple-500/25 hover:bg-gradient-to-r hover:from-[#5B2EFF] hover:via-[#8B5CF6] hover:to-[#FF4D8D] hover:border-transparent ${
                a.active ? `${GRADIENT} border-transparent shadow-lg shadow-purple-500/25` : 'bg-white border-purple-100 shadow-sm'
              }`}
            >
              <span className={`w-11 h-11 shrink-0 rounded-xl flex items-center justify-center transition-colors group-hover:!bg-white/20 group-hover:!text-white ${a.active ? 'bg-white/20 text-white' : 'bg-purple-50 text-[#7C3AED]'}`}>
                {a.icon}
              </span>
              <span className="min-w-0">
                <span className={`block text-sm font-bold group-hover:text-white ${a.active ? 'text-white' : 'text-[#0D1B3D]'}`}>{a.title}</span>
                <span className={`block text-xs group-hover:text-white/90 ${a.active ? 'text-white/90' : 'text-gray-500'}`}>{a.desc}</span>
              </span>
            </button>
          ))}
        </div>

        {view === 'tickets' ? (
          <MyTickets refreshKey={ticketsRefreshKey} />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-6 items-start">
            {/* Mobile: horizontal category chips */}
            <div className="lg:hidden -mx-4 px-4 flex gap-2 overflow-x-auto pb-1">
              {CATEGORIES.map((c) => {
                const active = !term && activeCategory === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => selectCategory(c.id)}
                    className={`shrink-0 text-sm font-semibold px-4 py-2 rounded-full transition-all ${
                      active ? `${GRADIENT} text-white shadow-md shadow-purple-500/25` : 'bg-white text-gray-600 border border-purple-100'
                    }`}
                  >
                    {c.title}
                  </button>
                );
              })}
            </div>

            {/* Desktop: sticky category list + help card */}
            <aside className="hidden lg:block lg:sticky lg:top-24 space-y-4">
              <nav className="bg-white border border-purple-100 rounded-2xl shadow-sm p-2">
                {CATEGORIES.map((c) => {
                  const active = !term && activeCategory === c.id;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => selectCategory(c.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-sm font-semibold transition-all duration-200 ${
                        active ? `${GRADIENT} text-white shadow-md shadow-purple-500/25` : 'text-gray-600 hover:bg-purple-50 hover:text-[#7C3AED]'
                      }`}
                    >
                      <span className={`w-8 h-8 shrink-0 rounded-lg flex items-center justify-center ${active ? 'bg-white/20 text-white' : c.color}`}>
                        {CategoryIcons[c.id]}
                      </span>
                      <span className="flex-1 min-w-0">{c.title}</span>
                      <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${active ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>
                        {counts[c.id] || 0}
                      </span>
                    </button>
                  );
                })}
              </nav>
              <HelpCard onContact={() => setModalOpen(true)} />
            </aside>

            {/* Questions */}
            <main className="min-w-0">
              <h2 className="text-xl font-bold mb-4 bg-gradient-to-r from-[#5B2EFF] via-[#8B5CF6] to-[#FF4D8D] bg-clip-text text-transparent">
                {listTitle}
              </h2>

              {visibleFaqs.length === 0 ? (
                <div className="bg-white border border-purple-100 rounded-2xl p-10 text-center">
                  <p className="text-sm font-semibold text-[#0D1B3D]">No matching questions found</p>
                  <p className="text-sm text-gray-500 mt-1">Try a different word, or send your question to our team.</p>
                  <button
                    type="button"
                    onClick={() => setModalOpen(true)}
                    className={`mt-4 inline-flex items-center gap-2 ${GRADIENT} text-white text-sm font-bold px-5 py-2.5 rounded-full shadow-lg shadow-purple-500/25 hover:scale-[1.02] transition-all duration-200`}
                  >
                    <MessageIcon /> Contact Support
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {visibleFaqs.map((item) => (
                    <FaqItem
                      key={item.q}
                      item={item}
                      showCategory={!!term || activeCategory === 'all'}
                      isOpen={openKey === item.q}
                      onToggle={() => setOpenKey(openKey === item.q ? null : item.q)}
                    />
                  ))}
                </div>
              )}

              <div className="lg:hidden mt-6">
                <HelpCard onContact={() => setModalOpen(true)} />
              </div>
            </main>
          </div>
        )}
      </div>

      <ContactSupportModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        role="job_seeker"
        onCreated={() => {
          setTicketsRefreshKey((k) => k + 1);
          setView('tickets');
        }}
      />
    </div>
  );
}

export default JobSeekerHelpSupport;